import { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Copy, Plus, Trash2, MoreVertical, CheckCircle2, Loader2, RefreshCw, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { apiKeysApi } from "@/lib/api";
import { toast } from "sonner";
import { HelpIcon } from "@/components/HelpIcon";

const ALL_PERMISSIONS = [
  { id: "random:read", label: "Random Bytes", desc: "QRNG byte generation" },
  { id: "random:stream", label: "Stream", desc: "Streaming entropy" },
  { id: "vrf:generate", label: "VRF Generate", desc: "VRF proof generation" },
  { id: "vrf:verify", label: "VRF Verify", desc: "VRF verification" },
  { id: "stats:read", label: "Stats", desc: "Platform statistics" },
  { id: "health:read", label: "Health", desc: "Health checks" },
];

function formatRelative(isoStr) {
  if (!isoStr) return "—";
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function ApiKeys() {
  const { t } = useLanguage();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [revoking, setRevoking] = useState(null);

  const [form, setForm] = useState({
    name: "", description: "", environment: "development",
    permissions: ["random:read"], rate_limit: 1000,
  });

  const loadKeys = async () => {
    try {
      const data = await apiKeysApi.list();
      setKeys(data);
    } catch (err) {
      toast.error("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadKeys(); }, []);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (form.permissions.length === 0) { toast.error("Select at least one permission"); return; }
    setLoading(true);
    try {
      const result = await apiKeysApi.create(form);
      setCreatedKey(result.key);
      setShowCreate(false);
      await loadKeys();
      toast.success("API key created");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (keyId, name) => {
    if (!window.confirm(`Revoke "${name}"? This cannot be undone.`)) return;
    setRevoking(keyId);
    try {
      await apiKeysApi.revoke(keyId);
      setKeys(k => k.map(x => x.id === keyId ? { ...x, is_active: false } : x));
      toast.success("API key revoked");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRevoking(null);
    }
  };

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const togglePerm = (perm) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(perm)
        ? f.permissions.filter(p => p !== perm)
        : [...f.permissions, perm],
    }));
  };

  const resetForm = () => setForm({ name: "", description: "", environment: "development", permissions: ["random:read"], rate_limit: 1000 });

  return (
    <div data-testid="api-keys-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="api-keys-title">API Keys</h1>
            <HelpIcon featureTag="api_keys" portalType="customer" />
          </div>
          <p className="text-sm text-slate-500 mt-1">Manage your API keys for accessing quantum entropy services</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadKeys} className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors rounded" data-testid="refresh-keys-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => { resetForm(); setShowCreate(true); }} className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 transition-colors uppercase tracking-widest text-xs font-bold px-5 py-2.5" data-testid="create-api-key-btn">
            <Plus className="w-4 h-4" /> New API Key
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200" data-testid="api-keys-table">
        {loading && keys.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading...
          </div>
        ) : keys.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <AlertCircle className="w-8 h-8 mb-2 opacity-40" />
            <p className="text-sm">No API keys yet. Create your first key.</p>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-[1fr_140px_110px_160px_100px_90px_80px_44px] bg-slate-50 border-b border-slate-200">
              {["Name","Key Prefix","Environment","Permissions","Rate Limit","Last Used","Status",""].map((h,i) => (
                <div key={i} className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
              ))}
            </div>
            {keys.map((key) => (
              <div key={key.id} className={`grid grid-cols-[1fr_140px_110px_160px_100px_90px_80px_44px] border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${!key.is_active ? "opacity-50" : ""}`} data-testid={`key-row-${key.id}`}>
                <div className="px-4 py-3 flex items-center">
                  <div>
                    <div className="font-medium text-slate-900 text-sm">{key.name}</div>
                    {key.description && <div className="text-xs text-slate-400 mt-0.5 truncate max-w-[160px]">{key.description}</div>}
                  </div>
                </div>
                <div className="px-4 py-3 flex items-center gap-1">
                  <code className="text-xs font-mono text-slate-700 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">{key.key_prefix}...</code>
                  <button onClick={() => copy(key.key_prefix, key.id)} className="text-slate-400 hover:text-slate-600" data-testid={`copy-key-${key.id}`}>
                    {copiedId === key.id ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <div className="px-4 py-3 flex items-center">
                  <Badge variant="outline" className={`text-xs capitalize border ${
                    key.environment === "production" ? "border-emerald-200 text-emerald-700 bg-emerald-50" :
                    key.environment === "staging" ? "border-amber-200 text-amber-700 bg-amber-50" :
                    "border-slate-200 text-slate-600"
                  }`}>{key.environment}</Badge>
                </div>
                <div className="px-4 py-3 flex items-center">
                  <div className="flex flex-wrap gap-1">
                    {key.permissions.slice(0, 2).map(p => (
                      <Badge key={p} variant="outline" className="text-[10px] py-0 border-slate-200 text-slate-500">{p}</Badge>
                    ))}
                    {key.permissions.length > 2 && (
                      <Badge variant="outline" className="text-[10px] py-0 border-slate-200 text-slate-500">+{key.permissions.length - 2}</Badge>
                    )}
                  </div>
                </div>
                <div className="px-4 py-3 text-sm text-slate-600 font-mono tabular-nums flex items-center">{key.rate_limit.toLocaleString()}/s</div>
                <div className="px-4 py-3 text-xs text-slate-400 flex items-center">{formatRelative(key.last_used)}</div>
                <div className="px-4 py-3 flex items-center">
                  <Badge className={`text-xs ${key.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`} variant="outline">
                    {key.is_active ? "Active" : "Revoked"}
                  </Badge>
                </div>
                <div className="px-4 py-3 flex items-center">
                  {key.is_active && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 text-slate-400 hover:text-slate-600" data-testid={`key-menu-${key.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleRevoke(key.id, key.name)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`revoke-key-${key.id}`}>
                          {revoking === key.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                          Revoke Key
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="create-key-dialog">
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>Configure your new API key. The key will only be shown once.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Production API" className="mt-1.5" data-testid="key-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Description</Label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional description" className="mt-1.5" data-testid="key-desc-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Environment</Label>
              <Select value={form.environment} onValueChange={v => setForm(f => ({ ...f, environment: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="key-env-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["development", "staging", "production"].map(e => (
                    <SelectItem key={e} value={e} className="capitalize">{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 block mb-2">Permissions *</Label>
              <div className="space-y-2">
                {ALL_PERMISSIONS.map(p => (
                  <div key={p.id} className="flex items-start gap-2.5">
                    <Checkbox id={p.id} checked={form.permissions.includes(p.id)} onCheckedChange={() => togglePerm(p.id)} className="mt-0.5" data-testid={`perm-${p.id}`} />
                    <div>
                      <label htmlFor={p.id} className="text-sm font-medium text-slate-800 cursor-pointer">{p.label}</label>
                      <p className="text-xs text-slate-400">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 transition-colors rounded">Cancel</button>
            <button onClick={handleCreate} disabled={loading} className="px-4 py-2 bg-slate-900 text-white text-sm hover:bg-slate-800 transition-colors rounded flex items-center gap-2 disabled:opacity-60" data-testid="confirm-create-key-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Create Key
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New key reveal dialog */}
      <Dialog open={!!createdKey} onOpenChange={() => setCreatedKey(null)}>
        <DialogContent className="max-w-md" data-testid="new-key-reveal-dialog">
          <DialogHeader>
            <DialogTitle>API Key Created</DialogTitle>
            <DialogDescription className="text-amber-600 font-medium">Copy this key now — it will not be shown again.</DialogDescription>
          </DialogHeader>
          <div className="bg-slate-900 text-cyan-400 font-mono text-sm px-4 py-3 rounded-lg break-all select-all" data-testid="new-key-value">
            {createdKey}
          </div>
          <div className="flex gap-2">
            <button onClick={() => copy(createdKey, "new")} className="flex-1 flex items-center justify-center gap-2 border border-slate-200 text-sm text-slate-700 hover:bg-slate-50 px-4 py-2 rounded transition-colors" data-testid="copy-new-key-btn">
              {copiedId === "new" ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
              {copiedId === "new" ? "Copied!" : "Copy Key"}
            </button>
            <button onClick={() => setCreatedKey(null)} className="flex-1 bg-slate-900 text-white text-sm hover:bg-slate-800 px-4 py-2 rounded transition-colors" data-testid="done-key-btn">
              Done
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
